import React, { createContext, useContext, useState, useMemo } from "react";
import PropTypes from "prop-types";

/**
 * Context for managing SideNav state
 * Provides state management for expanded items, navigation expansion, and panel sizing
 */
const SideNavStateContext = createContext();

/**
 * Hook to access the SideNav context
 *
 * @returns {Object} Context value containing state and setters
 * @throws {Error} When used outside of SideNavStateProvider
 */
export const useSideNavStateContext = () => {
  const context = useContext(SideNavStateContext);
  if (!context) {
    throw new Error(
      "useSideNavStateContext must be used within a SideNavStateProvider"
    );
  }
  return context;
};

/**
 * Provider component for SideNav state management
 *
 * Manages the following state:
 * - expandedItem: Currently expanded navigation item index (null when none)
 * - isExpanded: Whether the sidebar is expanded (collapsed shows icons only)
 * - expandedWidth: Width/height of the expanded panel (desktop: width, mobile: height)
 * - isLocked: Reserved for future use (e.g. lock sidebar open)
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {any} [props.initialViewing] - Deprecated; kept for backward compatibility
 * @returns {JSX.Element} Context provider wrapper
 */
export const SideNavStateProvider = ({ children }) => {
  const [expandedItem, setExpandedItem] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedWidth, setExpandedWidth] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      expandedItem,
      setExpandedItem,
      isExpanded,
      setIsExpanded,
      expandedWidth,
      setExpandedWidth,
      isLocked,
      setIsLocked,
    }),
    [expandedItem, isExpanded, expandedWidth, isLocked]
  );

  return (
    <SideNavStateContext.Provider value={value}>
      {children}
    </SideNavStateContext.Provider>
  );
};

SideNavStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialViewing: PropTypes.any, // Kept for backward compatibility
};

export default SideNavStateContext;
